import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import TherapyRow from 'src/components/rows/TherapyRow';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { TherapyListParam } from 'src/interfaces/therapyInterface';
import { UsersTableProps } from 'src/interfaces/usersInterface';
import { TherapyResponse } from 'src/redux/state.types';
import { getTherapyListUseCases } from 'src/services/therapy/useCases';
import useDebounce from 'src/hooks/useDebounce';
import TablePagination from '@mui/material/TablePagination';
import { setEditTherapistDetailView, setTherapistDetailView } from 'src/redux/slices/therapy.slice';

const TheraphyCards = (props: UsersTableProps) => {
  const therapyData: TherapyResponse = useAppSelector((state) => state.therapy.getTherapyList);
  const { color } = props;
  const { t } = useTranslation('therapy');
  const dispatch = useAppDispatch();

  const [currentPage, setCurrentPage] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const debouncedSearch = useDebounce(searchInput, 500); // 500ms delay

  const coachTableHeaders = [
    { title: t('firstCol'), id: 1, space: '20%' },
    { title: t('secondCol'), id: 2, space: '20%' },
    { title: t('thirdCol'), id: 3, space: '15%' },
    { title: t('fourthCol'), id: 4, space: '15%' },
    { title: t('fifthCol'), id: 5, space: '15%' },
    { title: t('sixthCol'), id: 6, space: '15%' },
  ];

  useEffect(() => {
    dispatch(
      getTherapyListUseCases({ Name: debouncedSearch, page: currentPage + 1, limit: rowsPerPage }),
    );
    dispatch(setTherapistDetailView(''));
    dispatch(setEditTherapistDetailView(''));
  }, [currentPage, dispatch, debouncedSearch, rowsPerPage]);

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  return (
    <>
      <div
        className={`relative flex flex-col min-w-0 break-words w-full shadow-lg rounded-2xl bg-white text-primary "
          ${color === 'light' ? 'bg-white' : 'bg-sky-900 text-white'}`}
      >
        <div className='rounded-t mb-0 px-4 py-3 border-0'>
          <div className='flex flex-wrap items-center'>
            <div className='flex justify-between relative w-full px-4 max-w-full flex-grow flex-1'>
              <span className='flex'>
                <div className={`him-coaches mr-2 text-lg`}>{t('header')}</div>
                <Link to='/admin/therapy/add-therapy' className='linkBtn'>
                  +
                </Link>
              </span>
              <div>
                <input
                  className='border-0 px-2 py-1 placeholder-blueGray-300 text-blueGray-600 bg-white rounded-full text-sm shadow focus:ring-0 focus:outline-2 focus:border focus:border-secondary transition-all duration-150'
                  type='text'
                  name='search the therapist'
                  placeholder='Search the therapist'
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
        <div className='block w-full overflow-x-auto'>
          <div className='items-center bg-transparent border-collapse'>
            <div className='flex'>
              {coachTableHeaders.map((header) => (
                <div
                  key={header.id}
                  style={{ width: header.space }}
                  className={`usersTableHeader " +
                    ${
                      color === 'light'
                        ? 'bg-blueGray-50 text-blueGray-500 border-blueGray-100'
                        : 'bg-sky-800 text-sky-300 border-sky-700'
                    })`}
                >
                  {header.title}
                </div>
              ))}
            </div>
            <div className='card-body'>
              {therapyData.data.map((therapy: TherapyListParam) => (
                <TherapyRow
                  key={therapy.id}
                  profilePicture={therapy.profilePicture}
                  fullName={therapy.fullName}
                  email={therapy.email}
                  phone={therapy.phone}
                  address={therapy.address}
                  id={therapy.id}
                  status={therapy.status}
                  rating={therapy.rating}
                />
              ))}
            </div>
            <div className='him-pagination'>
              <TablePagination
                component='div'
                count={therapyData.metadata?.totalCount || 0}
                page={currentPage}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TheraphyCards;
